import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { LayoutGrid, List, Plus, Trash2, Star, Check, X, Mic, Play, FileText, ExternalLink } from 'lucide-react';
import { IconButton, PillButton, tint } from '../../components/ui/PanelControls';
import { STICKY, shotTilt } from '../storyboard/boardTokens';
import {
  listAssets,
  setAssetCanonical,
  deleteAsset,
  confirmAssetEntity,
  tagAssetWithEntity,
  listProjectEntities,
} from '../editor/shortformEditor/assetClient';
import apiClient from '../../services/apiClient';
import { AddAssetPopover, KIND_PRESETS, isAudioKind, isDocumentAsset } from './AddAssetPopover';
import { isVideoUrl, getEmbedUrl } from './assetMediaUtils';
import AssetPreviewModal from './AssetPreviewModal';

// Signed-contract status chip colours -- neutral by default, one accent per
// terminal state (see memory feedback-tarr-ui-style: colour only in status).
const CONTRACT_STATUS_LABEL = {
  draft: 'Draft', sent: 'Sent', delivered: 'Delivered',
  completed: 'Signed', declined: 'Declined', voided: 'Voided',
};
const contractStatusColor = (status, secondary) =>
  status === 'completed' ? '#2e7d32' : status === 'declined' || status === 'voided' ? '#c0392b' : secondary;

/**
 * AssetLibraryContent — flat grid/list of a project's assets, filterable by
 * kind (pill tabs, same PillButton filter-row pattern used elsewhere in this
 * app, e.g. StoryboardFloatingControls), with a compact "+ Add" popover for
 * creating one (search/upload/link/drive) instead of a permanent inline form.
 * No forced folder drill-down -- everything (view toggle, filters, add) is
 * visible in the one view, per explicit direction against over-navigating.
 *
 * Shared by AssetLibraryPage.jsx (direct-URL deep link) and
 * AssetLibraryDrawer.jsx (global drawer, openable from anywhere via
 * window.__openAssetLibrary(projectId)).
 * See openspec/changes/shortform-editor-real-assets.
 *
 * Auto-detect + confirm chips / sticky-note tag chips: every entity tag on
 * one underlying image is its own Asset row sharing one `imageKey`
 * (tarr-agent's vision-LLM auto-tag pipeline, and this file's own "+"
 * add-tag action, both rely on that convention -- see server/routes/
 * assets.js's POST /:id/tag doc comment). Sibling rows for the same
 * `imageKey` are grouped into ONE visual card (`groupedAssets` below)
 * instead of showing the same image N times, one per tag -- each tag then
 * renders as its own floating sticky note (grid) / inline chip (list),
 * pending ones (Asset.entityConfirmed === false, an unconfirmed vision-LLM
 * guess) showing confirm/reject actions instead of the plain canonical star.
 */
const AssetLibraryContent = ({ projectId, isDarkMode }) => {
  const c = tint(isDarkMode);

  const [assets, setAssets] = useState([]);
  const [entities, setEntities] = useState([]);
  const [kindFilter, setKindFilter] = useState('');
  const [view, setView] = useState('grid'); // 'grid' | 'list'
  const [showAddForm, setShowAddForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [previewAsset, setPreviewAsset] = useState(null);
  const [contractStatusByAssetId, setContractStatusByAssetId] = useState({});
  const addButtonRef = useRef(null);

  const reload = useCallback(async () => {
    if (!projectId) return;
    try {
      const [assetRows, entityRows] = await Promise.all([
        listAssets({ projectId }),
        listProjectEntities(projectId),
      ]);
      setAssets(assetRows || []);
      setEntities(entityRows || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
    // Signature status for 'contract' assets -- best-effort, never blocks the
    // library (DocuSign may be unconfigured / the user may not have connected).
    try {
      const { signatures = [] } = await apiClient.get(`/api/docusign/signatures?projectId=${encodeURIComponent(projectId)}`);
      const map = {};
      for (const s of signatures) {
        if (s.signedAssetId) map[s.signedAssetId] = s.status;
      }
      setContractStatusByAssetId(map);
    } catch { /* ignore */ }
  }, [projectId]);

  useEffect(() => { reload(); }, [reload]);

  // Story Ledger entity -> Asset kind, mirrors tarr-agent's own
  // _ENTITY_TYPE_TO_ASSET_KIND (storyboard_generation.py) -- used only to
  // look up an entity's own CANONICAL reference asset as a preview
  // thumbnail for the "+" add-tag picker below. A StoryLedgerEntry has no
  // dedicated portrait/image field of its own (by design -- not adding one
  // for this).
  const entityThumbnailById = useMemo(() => {
    const map = {};
    for (const entity of entities) {
      const kind = { character: 'character-ref', prop: 'prop-ref', location: 'location-ref' }[entity.entityType];
      const canonical = assets.find((a) => a.entityId === entity.id && a.isCanonical && (!kind || a.kind === kind));
      if (canonical) map[entity.id] = canonical.imageUrl;
    }
    return map;
  }, [entities, assets]);

  const visibleAssets = kindFilter ? assets.filter((a) => a.kind === kindFilter) : assets;
  const kindsInUse = useMemo(() => Array.from(new Set(assets.map((a) => a.kind))).sort(), [assets]);

  // Group sibling Asset rows that share one imageKey into a single visual
  // card -- `primary` drives the card's own image + delete action (the row
  // with no entityId, i.e. the plain asset itself, if one exists; otherwise
  // just the first row), `tags` is every row that DOES carry an entityId
  // (each becomes its own floating note / inline chip).
  const groupedAssets = useMemo(() => {
    const order = [];
    const byKey = new Map();
    for (const asset of visibleAssets) {
      const key = asset.imageKey || asset.id;
      if (!byKey.has(key)) { byKey.set(key, []); order.push(key); }
      byKey.get(key).push(asset);
    }
    return order.map((key) => {
      const rows = byKey.get(key);
      const primary = rows.find((r) => !r.entityId) || rows[0];
      const tags = rows.filter((r) => r.entityId);
      return { key, primary, tags };
    });
  }, [visibleAssets]);

  const handleMakeCanonical = useCallback(async (asset) => {
    try {
      await setAssetCanonical(asset.id, true);
      await reload();
    } catch (err) {
      setError(err.message);
    }
  }, [reload]);

  const handleDelete = useCallback(async (asset) => {
    try {
      await deleteAsset(asset.id);
      setAssets((prev) => prev.filter((a) => a.id !== asset.id));
    } catch (err) {
      setError(err.message);
    }
  }, []);

  // Confirms a pending auto-detected entity tag -- the checkmark on a
  // pending sticky note.
  const handleConfirmEntity = useCallback(async (asset) => {
    try {
      await confirmAssetEntity(asset.id);
      setAssets((prev) => prev.map((a) => (a.id === asset.id ? { ...a, entityConfirmed: true } : a)));
    } catch (err) {
      setError(err.message);
    }
  }, []);

  // Adds a NEW tag to an existing image -- the "+" affordance's picker
  // calls this once an entity is picked.
  const handleAddTag = useCallback(async (asset, entityId) => {
    try {
      const created = await tagAssetWithEntity(asset.id, entityId);
      setAssets((prev) => [...prev, created]);
    } catch (err) {
      setError(err.message);
    }
  }, []);

  if (!projectId) {
    return <div style={{ fontSize: '12px', color: c.secondary, padding: '20px' }}>Open a project first to see its Asset Library.</div>;
  }

  return (
    <div>
      {/* Toolbar -- view toggle + filters + add, all visible at once. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '12px', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: '2px' }}>
          <IconButton isDarkMode={isDarkMode} active={view === 'grid'} onClick={() => setView('grid')} title="Grid view">
            <LayoutGrid size={14} />
          </IconButton>
          <IconButton isDarkMode={isDarkMode} active={view === 'list'} onClick={() => setView('list')} title="List view">
            <List size={14} />
          </IconButton>
        </div>

        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', flex: 1 }}>
          <PillButton isDarkMode={isDarkMode} active={!kindFilter} onClick={() => setKindFilter('')}>All ({assets.length})</PillButton>
          {kindsInUse.map((kind) => (
            <PillButton key={kind} isDarkMode={isDarkMode} active={kindFilter === kind} onClick={() => setKindFilter(kind)}>
              {kind} ({assets.filter((a) => a.kind === kind).length})
            </PillButton>
          ))}
        </div>

        <div style={{ position: 'relative' }}>
          <PillButton ref={addButtonRef} isDarkMode={isDarkMode} icon={Plus} active={showAddForm} onClick={() => setShowAddForm((v) => !v)}>
            Add
          </PillButton>
          {showAddForm && (
            <AddAssetPopover
              isDarkMode={isDarkMode}
              c={c}
              projectId={projectId}
              entities={entities}
              defaultKind={kindFilter || KIND_PRESETS[0]}
              onClose={() => setShowAddForm(false)}
              onCreated={() => { reload(); setShowAddForm(false); }}
            />
          )}
        </div>
      </div>

      {error && <div style={{ color: '#ff6b6b', fontSize: '12px', marginBottom: '12px' }}>{error}</div>}

      {loading ? (
        <div style={{ fontSize: '12px', color: c.secondary }}>Loading…</div>
      ) : visibleAssets.length === 0 ? (
        <div style={{ fontSize: '12px', color: c.secondary }}>No assets yet — click Add above.</div>
      ) : view === 'grid' ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))', gap: '10px', paddingTop: '20px' }}>
          {groupedAssets.map((group) => (
            <AssetGridCard
              key={group.key}
              group={group}
              isDarkMode={isDarkMode}
              c={c}
              entities={entities}
              entityThumbnailById={entityThumbnailById}
              onMakeCanonical={handleMakeCanonical}
              onDelete={handleDelete}
              onConfirmEntity={handleConfirmEntity}
              onAddTag={handleAddTag}
              onOpenPreview={setPreviewAsset}
              contractStatus={contractStatusByAssetId[group.primary.id]}
            />
          ))}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {groupedAssets.map((group) => (
            <AssetListRow
              key={group.key}
              group={group}
              isDarkMode={isDarkMode}
              c={c}
              entities={entities}
              entityThumbnailById={entityThumbnailById}
              onMakeCanonical={handleMakeCanonical}
              onDelete={handleDelete}
              onConfirmEntity={handleConfirmEntity}
              onAddTag={handleAddTag}
              onOpenPreview={setPreviewAsset}
              contractStatus={contractStatusByAssetId[group.primary.id]}
            />
          ))}
        </div>
      )}

      <AssetPreviewModal asset={previewAsset} isDarkMode={isDarkMode} onClose={() => setPreviewAsset(null)} />
    </div>
  );
};

// Small icon-only button used INSIDE a sticky note / inline chip (confirm /
// reject / canonical-star) -- deliberately not IconButton (that component's
// 28px/22px fixed sizing and hover-background swap reads as a normal toolbar
// button, too heavy for a tiny label sitting on a 1-line note).
const noteIconButtonStyle = {
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  width: '14px', height: '14px', padding: 0, border: 'none', borderRadius: '3px',
  background: 'transparent', cursor: 'pointer', flexShrink: 0,
};

// One entity tag, floating above the grid card's top-left corner like
// ShotThumbnail.jsx's title sticky note (position: absolute, tilted via
// shotTilt, same STICKY tokens) -- NOT a flat inline chip. `index` staggers
// multiple notes on the same card with a small cascading offset + increasing
// z-index, the same "several overlapping corkboard notes" read a real stack
// of sticky notes has.
const EntityTagNote = ({ tag, index, onConfirm, onReject, onMakeCanonical }) => {
  const pending = tag.entityConfirmed === false;
  const tilt = shotTilt(tag.id);
  const name = tag.entity?.entityName || 'Unknown';
  return (
    <div
      title={pending ? `${name} (pending auto-tag — confirm or reject)` : name}
      style={{
        position: 'absolute', top: `${-15 - index * 4}px`, left: `${6 + index * 10}px`, zIndex: 10 + index,
        transform: `rotate(${tilt}deg)`, maxWidth: 'calc(100% - 14px)',
        display: 'flex', alignItems: 'center', gap: '3px',
        backgroundColor: pending ? STICKY.mount : STICKY.bg,
        border: `1px ${pending ? 'dashed' : 'solid'} ${STICKY.border}`,
        opacity: pending ? 0.92 : 1,
        borderRadius: '3px', padding: '3px 6px',
      }}
    >
      <span style={{
        fontSize: '10px', fontWeight: 700, color: STICKY.text, fontFamily: 'Inter, sans-serif',
        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '78px',
      }}>
        {name}
      </span>
      {pending ? (
        <span style={{ display: 'flex', gap: '2px' }}>
          <button type="button" onClick={onConfirm} title="Confirm this tag" style={{ ...noteIconButtonStyle, color: STICKY.text }}>
            <Check size={9} />
          </button>
          <button type="button" onClick={onReject} title="Reject this tag" style={{ ...noteIconButtonStyle, color: STICKY.text }}>
            <X size={9} />
          </button>
        </span>
      ) : (
        <button
          type="button" onClick={onMakeCanonical}
          title={tag.isCanonical ? 'Canonical reference' : 'Make canonical'}
          style={{ ...noteIconButtonStyle, color: tag.isCanonical ? STICKY.text : STICKY.dim }}
        >
          <Star size={9} fill={tag.isCanonical ? 'currentColor' : 'none'} />
        </button>
      )}
    </div>
  );
};

// Searchable entity picker for the "+" add-tag affordance -- a small preview
// thumbnail (the entity's own canonical reference asset, if any -- see
// entityThumbnailById above) next to each name, fuzzy-filtered client-side
// as the user types (no dedicated search endpoint needed for a project's
// already-small entity list).
const EntityTagPicker = ({ isDarkMode, c, entities, entityThumbnailById, onPick, onClose }) => {
  const [query, setQuery] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    const onMouseDown = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [onClose]);

  const filtered = query.trim()
    ? entities.filter((e) => e.entityName.toLowerCase().includes(query.trim().toLowerCase()))
    : entities;

  return (
    <div
      ref={ref}
      onClick={(e) => e.stopPropagation()}
      style={{
        position: 'absolute', top: '100%', left: 0, marginTop: '4px', zIndex: 40,
        width: '200px', maxHeight: '260px', overflowY: 'auto', padding: '6px', borderRadius: '8px',
        backgroundColor: c.bg, border: `1px solid ${c.border}`,
        boxShadow: isDarkMode ? '0 8px 24px rgba(0,0,0,0.7)' : '0 8px 24px rgba(0,0,0,0.12)',
      }}
    >
      <input
        autoFocus
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Tag a character/prop…"
        style={{
          width: '100%', padding: '5px 7px', marginBottom: '6px', borderRadius: '5px', border: `1px solid ${c.border}`,
          background: c.bg, color: c.text, fontSize: '11px', fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box',
        }}
      />
      {filtered.length === 0 ? (
        <div style={{ fontSize: '11px', color: c.secondary, padding: '4px' }}>No matches</div>
      ) : filtered.map((entity) => (
        <button
          key={entity.id}
          type="button"
          onClick={() => onPick(entity.id)}
          style={{
            width: '100%', display: 'flex', alignItems: 'center', gap: '6px', textAlign: 'left',
            padding: '4px', border: 'none', borderRadius: '5px', background: 'transparent', cursor: 'pointer',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
        >
          {entityThumbnailById[entity.id] ? (
            <img src={entityThumbnailById[entity.id]} alt="" style={{ width: '22px', height: '22px', objectFit: 'cover', borderRadius: '4px', flexShrink: 0, border: `1px solid ${c.border}` }} />
          ) : (
            <div style={{ width: '22px', height: '22px', borderRadius: '4px', flexShrink: 0, border: `1px solid ${c.border}` }} />
          )}
          <span style={{ fontSize: '11px', color: c.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {entity.entityName} <span style={{ color: c.secondary }}>({entity.entityType})</span>
          </span>
        </button>
      ))}
    </div>
  );
};

const AssetGridCard = ({ group, isDarkMode, c, entities, entityThumbnailById, onMakeCanonical, onDelete, onConfirmEntity, onAddTag, onOpenPreview, contractStatus }) => {
  const { primary: asset, tags } = group;
  const [hovered, setHovered] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const isDocument = isDocumentAsset(asset);
  const isAudio = !isDocument && isAudioKind(asset.kind);
  // A pasted YouTube/Vimeo/Drive link (Asset Library's "Link" mode) has no
  // file extension -- can't be used as a thumbnail <img>/<video> src at all,
  // so it gets the same "play icon on a plain tile" treatment as audio
  // rather than a broken image. The full embed only loads in the preview
  // modal on click, not as a live thumbnail here (wasteful for a grid of
  // many cards).
  const embedUrl = !isAudio && !isDocument ? getEmbedUrl(asset.imageUrl) : null;
  const isVideo = !isAudio && !isDocument && !embedUrl && isVideoUrl(asset.imageUrl);
  const taggedEntityIds = useMemo(() => new Set(tags.map((t) => t.entityId)), [tags]);
  const openDocument = () => asset.imageUrl && window.open(asset.imageUrl, '_blank', 'noopener,noreferrer');

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{ display: 'flex', flexDirection: 'column', gap: '4px', position: 'relative', marginTop: '20px' }}
    >
      {tags.map((tag, idx) => (
        <EntityTagNote
          key={tag.id}
          tag={tag}
          index={idx}
          onConfirm={() => onConfirmEntity(tag)}
          onReject={() => onDelete(tag)}
          onMakeCanonical={() => onMakeCanonical(tag)}
        />
      ))}

      <div style={{ position: 'relative', borderRadius: '8px', overflow: 'hidden', border: `1px solid ${c.border}`, aspectRatio: '1' }}>
        {isDocument ? (
          <div
            onClick={openDocument}
            title="Open document"
            style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '6px', background: c.border + '22', cursor: 'pointer', padding: '8px', textAlign: 'center' }}
          >
            <FileText size={24} color={c.secondary} />
            {contractStatus && (
              <span style={{ fontSize: '9px', letterSpacing: '.08em', textTransform: 'uppercase', color: contractStatusColor(contractStatus, c.secondary) }}>
                {CONTRACT_STATUS_LABEL[contractStatus] || contractStatus}
              </span>
            )}
          </div>
        ) : isAudio ? (
          <div
            onClick={() => onOpenPreview(asset)}
            title="Play"
            style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: c.border + '22', cursor: 'pointer' }}
          >
            <Mic size={24} color={c.secondary} />
          </div>
        ) : embedUrl ? (
          <div
            onClick={() => onOpenPreview(asset)}
            title="Play"
            style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: c.border + '22', cursor: 'pointer' }}
          >
            <Play size={22} color={c.secondary} fill={c.secondary} />
          </div>
        ) : isVideo ? (
          <div onClick={() => onOpenPreview(asset)} title="Play" style={{ width: '100%', height: '100%', position: 'relative', cursor: 'pointer' }}>
            {/* eslint-disable-next-line jsx-a11y/media-has-caption -- muted, controls-less thumbnail only; real playback (with controls) happens in AssetPreviewModal */}
            <video src={asset.imageUrl} preload="metadata" muted playsInline style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.15)' }}>
              <Play size={22} color="#fff" fill="#fff" style={{ filter: 'drop-shadow(0 0 3px rgba(0,0,0,0.6))' }} />
            </div>
          </div>
        ) : (
          <img src={asset.imageUrl} alt={asset.label} onClick={() => onOpenPreview(asset)} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', cursor: 'pointer' }} />
        )}
        {hovered && (
          <div style={{ position: 'absolute', top: '4px', right: '4px', display: 'flex', gap: '4px' }}>
            {isDocument && (
              <IconButton isDarkMode={isDarkMode} size={22} onClick={openDocument} title="Open document">
                <ExternalLink size={12} />
              </IconButton>
            )}
            {!isAudio && !isDocument && (
              <IconButton isDarkMode={isDarkMode} size={22} onClick={() => setShowPicker((v) => !v)} title="Tag a character/prop">
                <Plus size={12} />
              </IconButton>
            )}
            <IconButton isDarkMode={isDarkMode} size={22} onClick={() => onDelete(asset)} title="Delete">
              <Trash2 size={12} />
            </IconButton>
          </div>
        )}
        {showPicker && (
          <EntityTagPicker
            isDarkMode={isDarkMode}
            c={c}
            entities={entities.filter((e) => !taggedEntityIds.has(e.id))}
            entityThumbnailById={entityThumbnailById}
            onPick={(entityId) => { onAddTag(asset, entityId); setShowPicker(false); }}
            onClose={() => setShowPicker(false)}
          />
        )}
      </div>
      <span style={{ fontSize: '11px', color: c.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{asset.label}</span>
      <span style={{ fontSize: '10px', color: c.secondary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{asset.kind}</span>
    </div>
  );
};

const AssetListRow = ({ group, isDarkMode, c, entities, entityThumbnailById, onMakeCanonical, onDelete, onConfirmEntity, onAddTag, onOpenPreview, contractStatus }) => {
  const { primary: asset, tags } = group;
  const [showPicker, setShowPicker] = useState(false);
  const isDocument = isDocumentAsset(asset);
  const isAudio = !isDocument && isAudioKind(asset.kind);
  // See AssetGridCard's identical comment -- a pasted link with no file
  // extension can't be used as a thumbnail src at all.
  const embedUrl = !isAudio && !isDocument ? getEmbedUrl(asset.imageUrl) : null;
  const isVideo = !isAudio && !isDocument && !embedUrl && isVideoUrl(asset.imageUrl);
  const taggedEntityIds = useMemo(() => new Set(tags.map((t) => t.entityId)), [tags]);
  const thumbStyle = { width: '32px', height: '32px', borderRadius: '5px', border: `1px solid ${c.border}`, flexShrink: 0, cursor: 'pointer' };
  const openDocument = () => asset.imageUrl && window.open(asset.imageUrl, '_blank', 'noopener,noreferrer');

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '6px 4px', borderBottom: `1px solid ${c.border}` }}>
      {isDocument ? (
        <div onClick={openDocument} title="Open document" style={{ ...thumbStyle, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <FileText size={14} color={c.secondary} />
        </div>
      ) : isAudio ? (
        <div onClick={() => onOpenPreview(asset)} title="Play" style={{ ...thumbStyle, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Mic size={14} color={c.secondary} />
        </div>
      ) : embedUrl ? (
        <div onClick={() => onOpenPreview(asset)} title="Play" style={{ ...thumbStyle, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Play size={13} color={c.secondary} fill={c.secondary} />
        </div>
      ) : isVideo ? (
        // Play icon omitted at this 32x32 size -- doesn't fit cleanly next to
        // an image row's thumbnail without looking cramped; the metadata-
        // preload thumbnail alone is enough of a "this is media" cue here.
        // eslint-disable-next-line jsx-a11y/media-has-caption -- muted thumbnail only, real playback is in AssetPreviewModal
        <video src={asset.imageUrl} preload="metadata" muted playsInline onClick={() => onOpenPreview(asset)} style={{ ...thumbStyle, objectFit: 'cover' }} />
      ) : (
        <img src={asset.imageUrl} alt={asset.label} onClick={() => onOpenPreview(asset)} style={{ ...thumbStyle, objectFit: 'cover' }} />
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: '12px', color: c.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{asset.label}</div>
        <div style={{ fontSize: '10px', color: c.secondary, display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap', marginTop: '2px' }}>
          <span>{asset.kind}</span>
          {contractStatus && (
            <span style={{ fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: contractStatusColor(contractStatus, c.secondary) }}>
              {CONTRACT_STATUS_LABEL[contractStatus] || contractStatus}
            </span>
          )}
          {tags.map((tag) => {
            const pending = tag.entityConfirmed === false;
            return (
              <span
                key={tag.id}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: '3px',
                  backgroundColor: pending ? STICKY.mount : STICKY.bg,
                  border: `1px ${pending ? 'dashed' : 'solid'} ${STICKY.border}`,
                  opacity: pending ? 0.92 : 1,
                  color: STICKY.text, borderRadius: '3px', padding: '1px 5px', fontSize: '9.5px', fontWeight: 700,
                }}
              >
                {tag.entity?.entityName || 'Unknown'}
                {pending ? (
                  <>
                    <button type="button" onClick={() => onConfirmEntity(tag)} title="Confirm" style={{ ...noteIconButtonStyle, color: STICKY.text }}><Check size={8} /></button>
                    <button type="button" onClick={() => onDelete(tag)} title="Reject" style={{ ...noteIconButtonStyle, color: STICKY.text }}><X size={8} /></button>
                  </>
                ) : (
                  <button
                    type="button" onClick={() => onMakeCanonical(tag)}
                    title={tag.isCanonical ? 'Canonical reference' : 'Make canonical'}
                    style={{ ...noteIconButtonStyle, color: tag.isCanonical ? STICKY.text : STICKY.dim }}
                  >
                    <Star size={8} fill={tag.isCanonical ? 'currentColor' : 'none'} />
                  </button>
                )}
              </span>
            );
          })}
        </div>
      </div>
      {isDocument && (
        <IconButton isDarkMode={isDarkMode} size={22} onClick={openDocument} title="Open document">
          <ExternalLink size={12} />
        </IconButton>
      )}
      {!isAudio && !isDocument && (
        <div style={{ position: 'relative' }}>
          <IconButton isDarkMode={isDarkMode} size={22} onClick={() => setShowPicker((v) => !v)} title="Tag a character/prop">
            <Plus size={12} />
          </IconButton>
          {showPicker && (
            <EntityTagPicker
              isDarkMode={isDarkMode}
              c={c}
              entities={entities.filter((e) => !taggedEntityIds.has(e.id))}
              entityThumbnailById={entityThumbnailById}
              onPick={(entityId) => { onAddTag(asset, entityId); setShowPicker(false); }}
              onClose={() => setShowPicker(false)}
            />
          )}
        </div>
      )}
      <IconButton isDarkMode={isDarkMode} size={22} onClick={() => onDelete(asset)} title="Delete">
        <Trash2 size={12} />
      </IconButton>
    </div>
  );
};


export default AssetLibraryContent;
